import apiClient from './client';
import type {
  User,
  Tenant,
  Distillery,
  DistilleryDashboard,
  Spirit,
  TastingEvent,
  EventSpirit,
  EventParticipant,
  EventResults,
  Venue,
  Menu,
  MenuCategory,
  MenuItem,
  APIKey,
  APIKeyCreateRequest,
  APIKeyCreateResponse,
  Webhook,
  WebhookDelivery,
  Recipe,
  Batch,
  DistillationLog,
  BrewMasterInsight,
  TrendAlert,
  SpiritTrend,
  PaginatedResponse,
  MenuRecipeIngredient,
  CreateRecipeIngredientRequest,
  SetRecipeRequest,
  RecipeCostCalculation,
  PourCostOverview,
  WareneinsatzReport,
  VenueTable,
  Reservation,
  TablePlanData,
  CreateTableRequest,
  UpdateTableRequest,
  CreateReservationRequest,
  UpdateReservationRequest,
  InviteUserRequest,
  UpdateUserRequest,
} from '../types';

// ==================== Auth API ====================

export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),

  logout: () => apiClient.post('/auth/logout'),

  register: (data: {
    tenant_name: string;
    subdomain: string;
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    tenant_type?: string;
  }) => apiClient.post('/auth/register', data),

  me: () => apiClient.get<{ user: User; tenant: Tenant }>('/auth/me'),

  refresh: () => apiClient.post('/auth/refresh'),
};

// ==================== Distillery API ====================

export const distilleryAPI = {
  getMyDistillery: () =>
    apiClient.get<Distillery>('/b2b/distillery'),

  getDashboard: () =>
    apiClient.get<DistilleryDashboard>('/b2b/distillery/dashboard'),

  createDistillery: (data: Partial<Distillery>) =>
    apiClient.post<Distillery>('/b2b/distillery', data),

  updateDistillery: (data: Partial<Distillery>) =>
    apiClient.put<Distillery>('/b2b/distillery', data),

  getSpirits: (page = 1, limit = 20) =>
    apiClient.get<PaginatedResponse<Spirit>>('/b2b/distillery/spirits', {
      params: { page, limit },
    }),

  createSpirit: (data: Partial<Spirit>) =>
    apiClient.post<Spirit>('/b2b/distillery/spirits', data),

  updateSpirit: (id: number, data: Partial<Spirit>) =>
    apiClient.put<Spirit>(`/b2b/distillery/spirits/${id}`, data),

  getPlans: () =>
    apiClient.get('/b2b/distillery/plans'),
};

// ==================== Trends API ====================

export const trendsAPI = {
  getTopSpirits: (limit = 10) =>
    apiClient.get<Spirit[]>('/b2b/trends/top-spirits', { params: { limit } }),

  getRisingSpirits: (limit = 10) =>
    apiClient.get<Spirit[]>('/b2b/trends/rising-spirits', { params: { limit } }),

  getTopRated: (limit = 10) =>
    apiClient.get<Spirit[]>('/b2b/trends/top-rated', { params: { limit } }),

  getSpiritTrends: (spiritId: number, days = 30) =>
    apiClient.get<SpiritTrend[]>(`/b2b/trends/spirit/${spiritId}`, {
      params: { days },
    }),

  getAlerts: () =>
    apiClient.get<TrendAlert[]>('/b2b/trends/alerts'),

  dismissAlert: (id: number) =>
    apiClient.post(`/b2b/trends/alerts/${id}/dismiss`),
};

// ==================== Events API ====================

export const eventsAPI = {
  listEvents: (page = 1, limit = 20) =>
    apiClient.get<PaginatedResponse<TastingEvent>>('/b2b/events', {
      params: { page, limit },
    }),

  getEvent: (id: number) =>
    apiClient.get<TastingEvent>(`/b2b/events/${id}`),

  createEvent: (data: Partial<TastingEvent>) =>
    apiClient.post<TastingEvent>('/b2b/events', data),

  updateEvent: (id: number, data: Partial<TastingEvent>) =>
    apiClient.put<TastingEvent>(`/b2b/events/${id}`, data),

  deleteEvent: (id: number) =>
    apiClient.delete(`/b2b/events/${id}`),

  publishEvent: (id: number) =>
    apiClient.post(`/b2b/events/${id}/publish`),

  startEvent: (id: number) =>
    apiClient.post(`/b2b/events/${id}/start`),

  endEvent: (id: number) =>
    apiClient.post(`/b2b/events/${id}/end`),

  // Spirits
  getEventSpirits: (eventId: number) =>
    apiClient.get<EventSpirit[]>(`/b2b/events/${eventId}/spirits`),

  addSpirit: (eventId: number, spiritId: number, data: Partial<EventSpirit>) =>
    apiClient.post(`/b2b/events/${eventId}/spirits`, { spirit_id: spiritId, ...data }),

  removeSpirit: (eventId: number, spiritId: number) =>
    apiClient.delete(`/b2b/events/${eventId}/spirits/${spiritId}`),

  // Participants
  getParticipants: (eventId: number) =>
    apiClient.get<EventParticipant[]>(`/b2b/events/${eventId}/participants`),

  // Results
  getResults: (eventId: number) =>
    apiClient.get<EventResults>(`/b2b/events/${eventId}/results`),

  getStats: (eventId: number) =>
    apiClient.get(`/b2b/events/${eventId}/stats`),

  getLiveState: (eventId: number) =>
    apiClient.get(`/b2b/events/${eventId}/live`),
};

// ==================== Venue API ====================

export const venueAPI = {
  listVenues: (page = 1, limit = 20) =>
    apiClient.get<PaginatedResponse<Venue>>('/b2b/venues', {
      params: { page, limit },
    }),

  getVenue: (id: number) =>
    apiClient.get<Venue>(`/b2b/venues/${id}`),

  createVenue: (data: Partial<Venue>) =>
    apiClient.post<Venue>('/b2b/venues', data),

  updateVenue: (id: number, data: Partial<Venue>) =>
    apiClient.put<Venue>(`/b2b/venues/${id}`, data),

  // Change single venue switches (owner/admin); omitted switches stay unchanged
  updateVenueSettings: (
    id: number,
    data: Partial<Pick<Venue, 'show_prices' | 'show_guest_bill' | 'show_waiter_name' | 'qr_headline' | 'qr_hint'>>
  ) =>
    apiClient.patch<Venue>(`/b2b/venues/${id}/settings`, data),

  deleteVenue: (id: number) =>
    apiClient.delete(`/b2b/venues/${id}`),

  getVenueStats: (id: number) =>
    apiClient.get(`/b2b/venues/${id}/stats`),

  getVenueSpirits: (id: number) =>
    apiClient.get<Spirit[]>(`/b2b/venues/${id}/spirits`),

  // Menus
  listMenus: (venueId: number) =>
    apiClient.get<Menu[]>(`/b2b/venues/${venueId}/menus`),

  createMenu: (data: Partial<Menu>) =>
    apiClient.post<Menu>('/b2b/menus', data),

  updateMenu: (id: number, data: Partial<Menu>) =>
    apiClient.put<Menu>(`/b2b/menus/${id}`, data),

  deleteMenu: (id: number) =>
    apiClient.delete(`/b2b/menus/${id}`),

  getMenu: (id: number) =>
    apiClient.get<Menu>(`/b2b/menus/${id}`),

  // Categories
  listCategories: (menuId: number) =>
    apiClient.get<MenuCategory[]>(`/b2b/menus/${menuId}/categories`),

  createCategory: (data: Partial<MenuCategory>) =>
    apiClient.post<MenuCategory>('/b2b/categories', data),

  updateCategory: (id: number, data: Partial<MenuCategory>) =>
    apiClient.put<MenuCategory>(`/b2b/categories/${id}`, data),

  deleteCategory: (id: number) =>
    apiClient.delete(`/b2b/categories/${id}`),

  reorderCategories: (menuId: number, categoryIds: number[]) =>
    apiClient.put(`/b2b/menus/${menuId}/categories/reorder`, { category_ids: categoryIds }),

  // Items
  listItems: (categoryId: number) =>
    apiClient.get<MenuItem[]>(`/b2b/categories/${categoryId}/items`),

  createItem: (data: Partial<MenuItem>) =>
    apiClient.post<MenuItem>('/b2b/menu-items', data),

  updateItem: (id: number, data: Partial<MenuItem>) =>
    apiClient.put<MenuItem>(`/b2b/menu-items/${id}`, data),

  // Uploads a photo and returns its URL; it is applied to the item when the item is saved
  uploadItemImage: (id: number, image: Blob) => {
    const fd = new FormData();
    fd.append('file', image, 'photo.jpg');
    return apiClient.post<{ url: string }>(`/b2b/menu-items/${id}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteItem: (id: number) =>
    apiClient.delete(`/b2b/menu-items/${id}`),

  setItemOutOfStock: (id: number, outOfStock: boolean) =>
    apiClient.put(`/b2b/menu-items/${id}/stock`, { is_out_of_stock: outOfStock }),

  reorderItems: (categoryId: number, itemIds: number[]) =>
    apiClient.put(`/b2b/categories/${categoryId}/items/reorder`, { item_ids: itemIds }),

  // Guest list
  getGuestList: (venueId: number) =>
    apiClient.get<GuestListEntry[]>(`/b2b/venues/${venueId}/guests`),

  // Add inventory items to menu
  addInventoryToMenu: (venueId: number, data: {
    category_id: number;
    inventory_item_ids: number[];
    price_strategy: 'sale_price' | 'purchase_price_with_markup';
    markup_percentage?: number;
    name_source: 'product_name' | 'spirit_name';
  }) => apiClient.post(`/b2b/venues/${venueId}/menu-items/from-inventory`, data),
};

// ==================== API Keys API ====================

export const apiKeysAPI = {
  listKeys: () =>
    apiClient.get<APIKey[]>('/b2b/api-keys'),

  getKey: (id: number) =>
    apiClient.get<APIKey>(`/b2b/api-keys/${id}`),

  createKey: (data: APIKeyCreateRequest) =>
    apiClient.post<APIKeyCreateResponse>('/b2b/api-keys', data),

  updateKey: (id: number, data: Partial<APIKey>) =>
    apiClient.put<APIKey>(`/b2b/api-keys/${id}`, data),

  deleteKey: (id: number) =>
    apiClient.delete(`/b2b/api-keys/${id}`),

  revokeKey: (id: number) =>
    apiClient.post(`/b2b/api-keys/${id}/revoke`),

  getKeyStats: (id: number) =>
    apiClient.get(`/b2b/api-keys/${id}/stats`),

  getKeyLogs: (id: number, page = 1, limit = 50) =>
    apiClient.get(`/b2b/api-keys/${id}/logs`, { params: { page, limit } }),

  listScopes: () =>
    apiClient.get<string[]>('/b2b/api-keys/scopes'),
};

// ==================== Webhooks API ====================

export const webhooksAPI = {
  listWebhooks: () =>
    apiClient.get<Webhook[]>('/b2b/webhooks'),

  getWebhook: (id: number) =>
    apiClient.get<Webhook>(`/b2b/webhooks/${id}`),

  createWebhook: (data: Partial<Webhook>) =>
    apiClient.post<Webhook>('/b2b/webhooks', data),

  updateWebhook: (id: number, data: Partial<Webhook>) =>
    apiClient.put<Webhook>(`/b2b/webhooks/${id}`, data),

  deleteWebhook: (id: number) =>
    apiClient.delete(`/b2b/webhooks/${id}`),

  activateWebhook: (id: number) =>
    apiClient.post(`/b2b/webhooks/${id}/activate`),

  deactivateWebhook: (id: number) =>
    apiClient.post(`/b2b/webhooks/${id}/deactivate`),

  testWebhook: (id: number) =>
    apiClient.post(`/b2b/webhooks/${id}/test`),

  regenerateSecret: (id: number) =>
    apiClient.post(`/b2b/webhooks/${id}/regenerate-secret`),

  getDeliveries: (id: number, page = 1, limit = 50) =>
    apiClient.get<PaginatedResponse<WebhookDelivery>>(`/b2b/webhooks/${id}/deliveries`, {
      params: { page, limit },
    }),

  listEventTypes: () =>
    apiClient.get<string[]>('/b2b/webhooks/event-types'),
};

// ==================== BrewMaster API ====================

export const brewmasterAPI = {
  // Recipes
  listRecipes: (distilleryId: number) =>
    apiClient.get<Recipe[]>(`/b2b/brewmaster/distillery/${distilleryId}/recipes`),

  getRecipe: (id: number) =>
    apiClient.get<Recipe>(`/b2b/brewmaster/recipes/${id}`),

  createRecipe: (distilleryId: number, data: Partial<Recipe>) =>
    apiClient.post<Recipe>(`/b2b/brewmaster/distillery/${distilleryId}/recipes`, data),

  updateRecipe: (id: number, data: Partial<Recipe>) =>
    apiClient.put<Recipe>(`/b2b/brewmaster/recipes/${id}`, data),

  deleteRecipe: (id: number) =>
    apiClient.delete(`/b2b/brewmaster/recipes/${id}`),

  forkRecipe: (distilleryId: number, recipeId: number) =>
    apiClient.post<Recipe>(`/b2b/brewmaster/distillery/${distilleryId}/recipes/${recipeId}/fork`),

  // Batches
  listBatches: (distilleryId: number, page = 1, limit = 20) =>
    apiClient.get<PaginatedResponse<Batch>>(`/b2b/brewmaster/distillery/${distilleryId}/batches`, {
      params: { page, limit },
    }),

  getActiveBatches: (distilleryId: number) =>
    apiClient.get<Batch[]>(`/b2b/brewmaster/distillery/${distilleryId}/batches/active`),

  getBatch: (id: number) =>
    apiClient.get<Batch>(`/b2b/brewmaster/batches/${id}`),

  createBatch: (distilleryId: number, data: Partial<Batch>) =>
    apiClient.post<Batch>(`/b2b/brewmaster/distillery/${distilleryId}/batches`, data),

  updateBatch: (id: number, data: Partial<Batch>) =>
    apiClient.put<Batch>(`/b2b/brewmaster/batches/${id}`, data),

  updateBatchStatus: (id: number, status: string) =>
    apiClient.put(`/b2b/brewmaster/batches/${id}/status`, { status }),

  // Distillation Logs
  getLogs: (batchId: number) =>
    apiClient.get<DistillationLog[]>(`/b2b/brewmaster/batches/${batchId}/logs`),

  addLog: (batchId: number, data: Partial<DistillationLog>) =>
    apiClient.post(`/b2b/brewmaster/batches/${batchId}/logs`, data),

  // Insights
  getInsights: () =>
    apiClient.get<BrewMasterInsight[]>('/b2b/brewmaster/insights'),

  getDistilleryInsights: (distilleryId: number) =>
    apiClient.get<BrewMasterInsight[]>(`/b2b/brewmaster/distillery/${distilleryId}/insights`),

  markInsightRead: (id: number) =>
    apiClient.put(`/b2b/brewmaster/insights/${id}/read`),

  dismissInsight: (id: number) =>
    apiClient.put(`/b2b/brewmaster/insights/${id}/dismiss`),

  // Dashboard
  getDashboard: (distilleryId: number) =>
    apiClient.get(`/b2b/brewmaster/distillery/${distilleryId}/dashboard`),
};

// ==================== Orders API ====================

export interface Order {
  id: number;
  uuid: string;
  tenant_id: number;
  venue_id: number;
  table_number: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'served' | 'cancelled';
  guest_email?: string;
  guest_name?: string;
  total_amount: number;
  currency: string;
  notes?: string;
  created_by_user_id?: number;
  order_source?: 'guest' | 'pos';
  created_by_user_name?: string;
  tab_id?: number;
  /** Staff member who took the ready order to the table ("Ich bringe es") */
  delivered_by_user_id?: number;
  delivered_by_user_name?: string;
  delivered_at?: string;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
  confirmed_at?: string;
  served_at?: string;
  cancelled_at?: string;
}

export interface DeliveryReportRow {
  user_id: number;
  user_name: string;
  deliveries: number;
  drinks: number;
  tables: number;
  avg_pickup_seconds?: number;
  waiter_calls: number;
  pos_orders: number;
  first_at?: string;
  last_at?: string;
}

export interface OrderItem {
  id: number;
  order_id: number;
  menu_item_id: number;
  spirit_id?: number;
  name: string;
  quantity: number;
  unit_price: number;
  notes?: string;
  is_rated: boolean;
  botanicals?: string[];
  menu_type?: string;
  recipe?: string;
  image_url?: string;
}

export interface CreateOrderRequest {
  venue_id: number;
  table_number: string;
  items: {
    menu_item_id: number;
    quantity: number;
    notes?: string;
  }[];
  guest_email?: string;
  guest_name?: string;
  notes?: string;
  newsletter_opt_in?: boolean;
  /** Same value on a retry → the server returns the first order instead of a duplicate */
  client_order_id?: string;
}

export interface CreateStaffOrderRequest {
  venue_id: number;
  table_number: string;
  items: {
    menu_item_id: number;
    quantity: number;
    notes?: string;
  }[];
  notes?: string;
}

export interface OrderStats {
  venue_id: number;
  total_orders: number;
  pending_orders: number;
  confirmed_orders: number;
  preparing_orders: number;
  served_orders: number;
  cancelled_orders: number;
  total_revenue: number;
  today_revenue: number;
  average_order: number;
  avg_confirm_time_minutes: number;
  avg_serve_time_minutes: number;
}

export const orderAPI = {
  // Public endpoints (no auth required)
  createPublicOrder: (data: CreateOrderRequest) =>
    apiClient.post<Order>('/public/orders', data),

  getPublicOrder: (uuid: string) =>
    apiClient.get<Order>(`/public/orders/${uuid}`),

  getOrderHistory: (email: string, venueId: number, limit = 20) =>
    apiClient.get<Order[]>('/public/orders/history', {
      params: { email, venue_id: venueId, limit },
    }),

  // Authenticated endpoints
  listOrders: (venueId: number, status?: string, limit = 50, offset = 0) =>
    apiClient.get<PaginatedResponse<Order>>('/b2b/orders', {
      params: { venue_id: venueId, status, limit, offset },
    }),

  getLiveOrders: (venueId: number) =>
    apiClient.get<Order[]>('/b2b/orders/live', {
      params: { venue_id: venueId },
    }),

  getOrder: (id: number) =>
    apiClient.get<Order>(`/b2b/orders/${id}`),

  updateOrderStatus: (id: number, status: string) =>
    apiClient.patch(`/b2b/orders/${id}/status`, { status }),

  // Logged-in staff member takes a ready order to the table ("Ich bringe es"); 409 if someone else already does
  claimDelivery: (id: number) =>
    apiClient.post<Order>(`/b2b/orders/${id}/claim-delivery`),

  // Undo "Ich bringe es" (own claim; owner/admin may undo any); 403 for someone else's claim
  unclaimDelivery: (id: number) =>
    apiClient.delete<Order>(`/b2b/orders/${id}/claim-delivery`),

  getPendingCount: (venueId: number) =>
    apiClient.get<{ pending_count: number }>('/b2b/orders/pending-count', {
      params: { venue_id: venueId },
    }),

  getOrderStats: (venueId: number) =>
    apiClient.get<OrderStats>('/b2b/orders/stats', {
      params: { venue_id: venueId },
    }),

  createStaffOrder: (data: CreateStaffOrderRequest) =>
    apiClient.post<Order>('/b2b/orders', data),
};

// ==================== Ratings API ====================

export interface SpiritRating {
  id: number;
  uuid: string;
  tenant_id: number;
  venue_id: number;
  spirit_id: number;
  order_item_id?: number;
  overall_rating: number;
  aroma_rating?: number;
  taste_rating?: number;
  finish_rating?: number;
  notes?: string;
  is_anonymous: boolean;
  guest_email?: string;
  guest_name?: string;
  created_at: string;
}

export interface CreateRatingRequest {
  spirit_id?: number;
  venue_id: number;
  order_item_id?: number;
  menu_item_name?: string;
  overall_rating: number;
  aroma_rating?: number;
  taste_rating?: number;
  finish_rating?: number;
  detected_botanicals?: string[];
  notes?: string;
  is_anonymous: boolean;
  guest_email?: string;
  guest_name?: string;
}

export interface SpiritRatingStats {
  spirit_id: number;
  total_ratings: number;
  avg_overall_rating: number;
  avg_aroma_rating: number;
  avg_taste_rating: number;
  avg_finish_rating: number;
  rating_distribution: Record<number, number>;
}

export const ratingAPI = {
  // Public endpoint (no auth required)
  createPublicRating: (data: CreateRatingRequest) =>
    apiClient.post<SpiritRating>('/public/ratings', data),

  // Authenticated endpoints
  listRatings: (venueId: number, spiritId?: number, limit = 20, offset = 0) =>
    apiClient.get<PaginatedResponse<SpiritRating>>('/b2b/ratings', {
      params: { venue_id: venueId, spirit_id: spiritId, limit, offset },
    }),

  getSpiritRatingStats: (spiritId: number) =>
    apiClient.get<SpiritRatingStats>(`/b2b/ratings/spirits/${spiritId}/stats`),
};

// ==================== Analytics API ====================

export interface TopSeller {
  name: string;
  spirit_id?: number;
  quantity_sold: number;
  revenue: number;
}

export interface SpiritRatingSummary {
  spirit_id: number;
  spirit_name: string;
  total_ratings: number;
  avg_rating: number;
  min_rating: number;
  max_rating: number;
}

export interface RevenueByPeriod {
  date: string;
  order_count: number;
  total_revenue: number;
}

export interface RatingWithSpirit {
  id: number;
  spirit_id: number;
  spirit_name: string;
  overall_rating: number;
  notes?: string;
  created_at: string;
}

export interface BotanicalStat {
  name: string;
  detection_count: number;
  percentage: number;
}

export interface GuestListEntry {
  email: string;
  name: string;
  order_count: number;
  total_spent: number;
  first_visit: string;
  last_visit: string;
  is_stammgast: boolean;
}

export interface GuestStats {
  total_unique_guests: number;
  stammgast_count: number;
  new_guests_count: number;
  returning_ratio: number;
}

export interface TastingSessionStats {
  total_sessions: number;
  completed_sessions: number;
  abandoned_sessions: number;
  active_sessions: number;
  completion_rate: number;
  avg_duration_min: number;
}

export interface TastingSpiritRanking {
  spirit_name: string;
  spirit_brand?: string;
  tasting_count: number;
  avg_overall: number;
  avg_aroma: number;
  avg_taste: number;
  avg_finish: number;
}

export interface DescriptorCount {
  name: string;
  count: number;
}

export interface TastingDescriptorStats {
  top_aroma: DescriptorCount[];
  top_taste: DescriptorCount[];
  top_finish: DescriptorCount[];
}

export interface TastingBotanicalAccuracy {
  total_detections: number;
  avg_accuracy: number;
  top_detected: DescriptorCount[];
}

export interface TastingFunnelStep {
  step: string;
  count: number;
}

export interface TastingAnalytics {
  session_stats: TastingSessionStats;
  spirit_ranking: TastingSpiritRanking[];
  descriptor_stats: TastingDescriptorStats;
  botanical_accuracy: TastingBotanicalAccuracy;
  completion_funnel: TastingFunnelStep[];
}

export interface VenueAnalytics {
  venue_id: number;
  order_stats: OrderStats;
  top_sellers: TopSeller[];
  rating_summaries: SpiritRatingSummary[];
  revenue_by_day: RevenueByPeriod[];
  recent_ratings: RatingWithSpirit[];
  botanical_stats: BotanicalStat[];
  guest_stats?: GuestStats;
  tasting_analytics?: TastingAnalytics;
}

export interface QRTableStat {
  table_number: number | null;
  today: number;
  this_week: number;
  this_month: number;
  total: number;
}

export const analyticsAPI = {
  getVenueAnalytics: (venueId: number) =>
    apiClient.get<VenueAnalytics>('/b2b/analytics', {
      params: { venue_id: venueId },
    }),
  getQRTableStats: (venueId: number) =>
    apiClient.get<QRTableStat[]>(`/b2b/venues/${venueId}/qr-stats`),
  // Work per waiter (deliveries, drinks, tables, pickup time, calls, POS); sinceHours 0 = all
  getDeliveryReport: (venueId: number, sinceHours = 0) =>
    apiClient.get<DeliveryReportRow[]>(`/b2b/venues/${venueId}/delivery-report`, {
      params: sinceHours > 0 ? { since_hours: sinceHours } : undefined,
    }),
};

// ==================== Venue Email Branding API (Phase 10.15) ====================

export interface VenueEmailSettings {
  id: number;
  venue_id: number;
  tenant_id: number;
  branding_enabled: boolean;
  from_name?: string | null;
  reply_to?: string | null;
  logo_url?: string | null;
  header_image_url?: string | null;
  primary_color?: string | null;
  intro_text?: string | null;
  footer_text?: string | null;
}

export const emailBrandingAPI = {
  getSettings: (venueId: number) =>
    apiClient.get<VenueEmailSettings>(`/b2b/venues/${venueId}/email-settings`),
  updateSettings: (venueId: number, data: Partial<VenueEmailSettings>) =>
    apiClient.put<VenueEmailSettings>(`/b2b/venues/${venueId}/email-settings`, data),
  uploadAsset: (venueId: number, type: 'logo' | 'header', file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<VenueEmailSettings>(
      `/b2b/venues/${venueId}/email-assets/${type}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// ==================== Push Notifications API ====================

export interface PushSubscription {
  uuid: string;
  is_active: boolean;
  preferences: NotificationPreferences;
  created_at: string;
}

export interface NotificationPreferences {
  orders: boolean;
  marketing: boolean;
  system: boolean;
}

export interface SubscribeRequest {
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  venue_id?: number;
  user_agent?: string;
  device_name?: string;
  guest_email?: string;
  guest_session_id?: string;
}

export interface UpdatePreferencesRequest {
  orders?: boolean;
  marketing?: boolean;
  system?: boolean;
}

export interface VAPIDKeyResponse {
  public_key: string;
}

export const pushAPI = {
  // Get VAPID public key (no auth required)
  getVAPIDKey: () =>
    apiClient.get<VAPIDKeyResponse>('/push/vapid-key'),

  // Subscribe (authenticated)
  subscribe: (data: SubscribeRequest) =>
    apiClient.post<PushSubscription>('/push/subscribe', data),

  // Unsubscribe (authenticated)
  unsubscribe: (endpoint: string) =>
    apiClient.post('/push/unsubscribe', { endpoint }),

  // List subscriptions (authenticated)
  listSubscriptions: () =>
    apiClient.get<PushSubscription[]>('/push/subscriptions'),

  // Update preferences (authenticated)
  updatePreferences: (id: number, data: UpdatePreferencesRequest) =>
    apiClient.put(`/push/subscriptions/${id}/preferences`, data),

  // Send test notification (authenticated)
  sendTestNotification: () =>
    apiClient.post('/push/test'),

  // Guest subscribe (no auth required)
  subscribeGuest: (data: SubscribeRequest) =>
    apiClient.post<PushSubscription>('/public/push/subscribe', data),

  // Guest unsubscribe (no auth required)
  unsubscribeGuest: (endpoint: string) =>
    apiClient.post('/public/push/unsubscribe', { endpoint }),
};

// ==================== Waiter Call API ====================

export type WaiterCallStatus = 'pending' | 'acknowledged' | 'resolved';
export type WaiterCallReason = 'service' | 'payment' | 'other';

export interface WaiterCall {
  id: number;
  uuid: string;
  tenant_id: number;
  venue_id: number;
  table_number: string;
  status: WaiterCallStatus;
  reason?: WaiterCallReason;
  guest_session_id?: string;
  acknowledged_by?: number;
  acknowledged_by_name?: string;
  acknowledged_at?: string;
  resolved_at?: string;
  created_at: string;
}

export interface CreateWaiterCallRequest {
  venue_id: number;
  table_number: string;
  guest_session_id?: string;
}

export interface WaiterCallResponse {
  uuid: string;
  table_number: string;
  status: WaiterCallStatus;
  created_at: string;
  acknowledged_at?: string;
}

// ==================== Pay at the table / Bill API (Phase 10.2) ====================

export interface GuestBillPayment {
  amount: number;
  tip_amount: number;
  payment_method: string;
  paid_at: string;
}

export interface GuestBillOrderItem {
  name: string;
  quantity: number;
  total_price?: number;
  unit_price?: number;
}

export interface GuestBillOrder {
  uuid?: string;
  status?: string;
  total_amount?: number;
  items?: GuestBillOrderItem[];
}

export interface GuestBill {
  id: number;
  uuid: string;
  table_number: string;
  status: string;
  total_amount: number;
  paid_amount: number;
  tip_amount: number;
  remaining_amount: number;
  currency: string;
  order_count: number;
  orders?: GuestBillOrder[];
  payments?: GuestBillPayment[];
}

export const billAPI = {
  // Public — guest views the open bill for their table
  getBill: (venueId: number, table: string) =>
    apiClient.get<GuestBill | null>('/public/bill', {
      params: { venue_id: venueId, table },
    }),
  // Public — guest requests the bill (sets tip + notifies staff)
  requestBill: (data: {
    venue_id: number;
    table_number: string;
    tip_amount?: number;
    guest_session_id?: string;
  }) => apiClient.post('/public/request-bill', data),
};

export const waiterCallAPI = {
  // Public endpoints (no auth required)
  createWaiterCall: (data: CreateWaiterCallRequest) =>
    apiClient.post<WaiterCallResponse>('/public/waiter-call', data),

  getWaiterCallStatus: (uuid: string) =>
    apiClient.get<WaiterCallResponse>(`/public/waiter-call/${uuid}`),

  // Authenticated endpoints (staff)
  listActiveWaiterCalls: (venueId: number) =>
    apiClient.get<WaiterCall[]>('/b2b/waiter-calls', {
      params: { venue_id: venueId },
    }),

  listAllWaiterCalls: (venueId: number, status?: WaiterCallStatus, limit = 50, offset = 0) =>
    apiClient.get<{ items: WaiterCall[]; total: number }>('/b2b/waiter-calls/all', {
      params: { venue_id: venueId, status, limit, offset },
    }),

  getWaiterCallCount: (venueId: number) =>
    apiClient.get<{ count: number }>('/b2b/waiter-calls/count', {
      params: { venue_id: venueId },
    }),

  acknowledgeWaiterCall: (id: number) =>
    apiClient.patch<WaiterCall>(`/b2b/waiter-calls/${id}/acknowledge`),

  resolveWaiterCall: (id: number) =>
    apiClient.patch<WaiterCall>(`/b2b/waiter-calls/${id}/resolve`),
};

// ==================== AI Barkeeper API ====================

export interface AIChatRequest {
  venue_id: number;
  message: string;
  conversation_id?: string;
}

export interface PairingRecommendation {
  tonics?: string[];
  garnishes?: string[];
  serving_tip?: string;
}

export interface RecipeIngredient {
  item: string;
  amount: string;
  available: boolean;
}

export interface CocktailRecipe {
  name: string;
  ingredients: RecipeIngredient[];
  instructions: string;
  glass?: string;
  garnish?: string;
}

export interface AIChatResponse {
  conversation_id: string;
  message: string;
  type: 'chat' | 'pairing' | 'cocktail';
  recommendations?: PairingRecommendation;
  recipe?: CocktailRecipe;
}

export interface AIStatusResponse {
  available: boolean;
  message: string;
}

export const aiBarkeeperAPI = {
  // Check if AI barkeeper is available
  getStatus: () =>
    apiClient.get<AIStatusResponse>('/public/ai/status'),

  // Send a chat message
  chat: (data: AIChatRequest) =>
    apiClient.post<AIChatResponse>('/public/ai/chat', data),
};

// ==================== Fine Tasting API ====================

export type TastingType = 'single' | 'comparison' | 'flight';
export type TastingStatus = 'active' | 'completed' | 'abandoned';
export type TastingStep = 'intro' | 'appearance' | 'aroma' | 'taste' | 'finish' | 'botanicals' | 'notes' | 'recommend' | 'complete';

export interface TastingSpirit {
  id: number;
  session_id: number;
  spirit_id?: number;
  menu_item_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  spirit_type?: string;
  known_botanicals?: string[];
  tasting_order: number;
  aroma_notes?: string;
  aroma_rating?: number;
  taste_notes?: string;
  taste_rating?: number;
  finish_notes?: string;
  finish_rating?: number;
  overall_rating?: number;
  detected_botanicals?: string[];
  botanical_accuracy?: number;
  ai_tasting_notes?: string;
  ai_flavor_profile?: Record<string, number>;
  ai_pairing_suggestions?: string[];
  started_at?: string;
  completed_at?: string;
}

export interface TastingSession {
  id: number;
  uuid: string;
  tenant_id: number;
  venue_id: number;
  table_number?: string;
  guest_session_id?: string;
  guest_name?: string;
  guest_email?: string;
  tasting_type: TastingType;
  status: TastingStatus;
  current_step: TastingStep;
  conversation_id?: string;
  spirits: TastingSpirit[];
  started_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TastingRecommendation {
  id: number;
  session_id: number;
  recommended_spirit_id?: number;
  recommended_menu_item_id?: number;
  spirit_name: string;
  reason: string;
  match_score: number;
  was_ordered: boolean;
}

export interface StartTastingRequest {
  venue_id: number;
  table_number?: string;
  guest_session_id?: string;
  guest_name?: string;
  guest_email?: string;
  tasting_type?: TastingType;
}

export interface StartTastingResponse {
  uuid: string;
  venue_id: number;
  tasting_type: TastingType;
  status: TastingStatus;
  current_step: TastingStep;
  spirits: TastingSpirit[];
  started_at: string;
  // Template/Prepared fields
  template_id?: number;
  prepared_tasting_id?: number;
  table_number?: string;
  custom_intro?: string;
}

export interface AddSpiritRequest {
  spirit_id?: number;
  menu_item_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  spirit_type?: string;
  known_botanicals?: string[];
}

export interface SubmitStepRequest {
  step: TastingStep;
  spirit_id?: number;
  notes?: string;
  rating?: number;
  descriptors?: string[];
}

export interface GetGuidanceRequest {
  step: TastingStep;
  spirit_id?: number;
}

export interface AIGuidanceResponse {
  guidance: string;
  questions: string[];
  hints: string[];
  suggested_descriptors: string[];
}

export interface SubmitBotanicalsRequest {
  spirit_id?: number;
  detected_botanicals: string[];
}

export interface BotanicalFeedbackResponse {
  feedback: string;
  correct: string[];
  close: string[];
  missed: string[];
  accuracy_score: number;
  educational_note?: string;
}

export interface GenerateNotesRequest {
  style?: 'professional' | 'casual' | 'educational';
  include_pairing?: boolean;
}

export interface AITastingNotesResponse {
  notes: string;
  flavor_wheel?: Record<string, number>;
  pairing_suggestions?: string[];
}

export interface GetRecommendationsRequest {
  limit?: number;
  available_only?: boolean;
}

export interface RecommendationResponse {
  spirit_name: string;
  spirit_id?: number;
  menu_item_id?: number;
  reason: string;
  match_score: number;
  flavor_profile?: Record<string, number>;
}

export interface TastingSummaryResponse {
  session: TastingSession;
  spirits: TastingSpirit[];
  recommendations: RecommendationResponse[];
  overall_notes?: string;
}

export const fineTastingAPI = {
  // Start a new tasting session
  startSession: (data: StartTastingRequest) =>
    apiClient.post<StartTastingResponse>('/public/tasting/start', data),

  // Get session status
  getSession: (uuid: string) =>
    apiClient.get<TastingSession>(`/public/tasting/${uuid}`),

  // Abandon session
  abandonSession: (uuid: string) =>
    apiClient.post(`/public/tasting/${uuid}/abandon`),

  // Add spirit to session
  addSpirit: (uuid: string, data: AddSpiritRequest) =>
    apiClient.post<TastingSpirit>(`/public/tasting/${uuid}/spirit`, data),

  // Submit step response
  submitStep: (uuid: string, data: SubmitStepRequest) =>
    apiClient.post(`/public/tasting/${uuid}/step`, data),

  // Complete session
  completeSession: (uuid: string) =>
    apiClient.post<TastingSummaryResponse>(`/public/tasting/${uuid}/complete`),

  // Get AI guidance for current step
  getGuidance: (uuid: string, data: GetGuidanceRequest) =>
    apiClient.post<AIGuidanceResponse>(`/public/tasting/${uuid}/ai/guide`, data),

  // Submit botanical detection for feedback
  detectBotanicals: (uuid: string, data: SubmitBotanicalsRequest) =>
    apiClient.post<BotanicalFeedbackResponse>(`/public/tasting/${uuid}/ai/botanicals`, data),

  // Generate AI tasting notes
  generateNotes: (uuid: string, data?: GenerateNotesRequest) =>
    apiClient.post<AITastingNotesResponse>(`/public/tasting/${uuid}/ai/notes`, data || {}),

  // Get spirit recommendations
  getRecommendations: (uuid: string, data?: GetRecommendationsRequest) =>
    apiClient.post<RecommendationResponse[]>(`/public/tasting/${uuid}/ai/recommend`, data || {}),

  // Template-based session starts
  startFromTemplate: (data: StartFromTemplateRequest) =>
    apiClient.post<StartTastingResponse>('/public/tasting/start-from-template', data),

  getPublicPreparedTasting: (uuid: string) =>
    apiClient.get<PreparedTastingResponse>(`/public/tasting/prepared/${uuid}`),

  startFromPrepared: (uuid: string, data?: StartFromPreparedRequest) =>
    apiClient.post<StartTastingResponse>(`/public/tasting/start-from-prepared/${uuid}`, data || {}),

  // List public templates for a venue
  listPublicTemplates: (venueId: number) =>
    apiClient.get<PublicTemplateResponse[]>(`/public/venues/${venueId}/tasting-templates`),
};

// ==================== Tasting Templates API ====================

export type PreparedTastingStatus = 'pending' | 'started' | 'completed' | 'expired';

export interface TastingTemplate {
  id: number;
  venue_id: number;
  name: string;
  description?: string;
  image_url?: string;
  tasting_type: TastingType;
  is_active: boolean;
  is_public: boolean;
  include_botanicals: boolean;
  include_ai_notes: boolean;
  custom_intro?: string;
  spirits?: TastingTemplateSpirit[];
  spirit_count: number;
  created_at: string;
  updated_at: string;
}

export interface TastingTemplateSpirit {
  id: number;
  menu_item_id: number;
  tasting_order: number;
  custom_notes?: string;
  spirit_name: string;
  spirit_brand?: string;
  image_url?: string;
  price?: number;
}

export interface PreparedTasting {
  id: number;
  uuid: string;
  venue_id: number;
  template_id?: number;
  template_name?: string;
  table_number?: string;
  guest_name?: string;
  notes?: string;
  status: PreparedTastingStatus;
  expires_at?: string;
  spirits?: PreparedTastingSpirit[];
  spirit_count: number;
  link: string;
  created_at: string;
  started_at?: string;
}

export interface PreparedTastingSpirit {
  id: number;
  menu_item_id: number;
  tasting_order: number;
  spirit_name: string;
  spirit_brand?: string;
  image_url?: string;
  price?: number;
}

export interface PublicTemplateResponse {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  tasting_type: TastingType;
  spirit_count: number;
  include_botanicals: boolean;
  custom_intro?: string;
}

export interface PreparedTastingResponse {
  id: number;
  uuid: string;
  venue_id: number;
  template_id?: number;
  template_name?: string;
  table_number?: string;
  guest_name?: string;
  notes?: string;
  status: PreparedTastingStatus;
  expires_at?: string;
  spirits?: PreparedTastingSpirit[];
  spirit_count: number;
  link: string;
  created_at: string;
  started_at?: string;
}

export interface CreateTastingTemplateRequest {
  venue_id: number;
  name: string;
  description?: string;
  image_url?: string;
  tasting_type?: TastingType;
  is_active?: boolean;
  is_public?: boolean;
  include_botanicals?: boolean;
  include_ai_notes?: boolean;
  custom_intro?: string;
}

export interface UpdateTastingTemplateRequest {
  name?: string;
  description?: string;
  image_url?: string;
  tasting_type?: TastingType;
  is_active?: boolean;
  is_public?: boolean;
  include_botanicals?: boolean;
  include_ai_notes?: boolean;
  custom_intro?: string;
}

export interface AddTemplateSpiritRequest {
  menu_item_id: number;
  tasting_order?: number;
  custom_notes?: string;
}

export interface ReorderTemplateSpiritsRequest {
  spirit_orders: Array<{
    spirit_id: number;
    tasting_order: number;
  }>;
}

export interface CreatePreparedTastingRequest {
  venue_id: number;
  template_id?: number;
  table_number?: string;
  guest_name?: string;
  notes?: string;
  menu_item_ids?: number[];
  spirits?: Array<{
    spirit_id?: number;
    menu_item_id?: number;
    tasting_order: number;
  }>;
}

export interface StartFromTemplateRequest {
  template_id: number;
  table_number?: string;
  guest_session_id?: string;
  guest_name?: string;
  guest_email?: string;
}

export interface StartFromPreparedRequest {
  guest_session_id?: string;
  guest_name?: string;
  guest_email?: string;
}

export const tastingTemplateAPI = {
  // Template Management (B2B Admin)
  listTemplates: (venueId: number, params?: { limit?: number; offset?: number; is_active?: boolean }) =>
    apiClient.get<PaginatedResponse<TastingTemplate>>(`/b2b/venues/${venueId}/tasting-templates`, { params }),

  createTemplate: (venueId: number, data: Omit<CreateTastingTemplateRequest, 'venue_id'>) =>
    apiClient.post<TastingTemplate>(`/b2b/venues/${venueId}/tasting-templates`, { ...data, venue_id: venueId }),

  getTemplate: (id: number) =>
    apiClient.get<TastingTemplate>(`/b2b/tasting-templates/${id}`),

  updateTemplate: (id: number, data: UpdateTastingTemplateRequest) =>
    apiClient.put<TastingTemplate>(`/b2b/tasting-templates/${id}`, data),

  deleteTemplate: (id: number) =>
    apiClient.delete(`/b2b/tasting-templates/${id}`),

  // Template Spirit Management
  addTemplateSpirit: (templateId: number, data: AddTemplateSpiritRequest) =>
    apiClient.post<TastingTemplateSpirit>(`/b2b/tasting-templates/${templateId}/spirits`, data),

  removeTemplateSpirit: (templateId: number, spiritId: number) =>
    apiClient.delete(`/b2b/tasting-templates/${templateId}/spirits/${spiritId}`),

  reorderTemplateSpirits: (templateId: number, data: ReorderTemplateSpiritsRequest) =>
    apiClient.put(`/b2b/tasting-templates/${templateId}/spirits/reorder`, data),

  // Prepared Tastings (Waiter Workflow)
  listPreparedTastings: (venueId: number, params?: { status?: PreparedTastingStatus }) =>
    apiClient.get<PreparedTasting[]>(`/b2b/venues/${venueId}/prepared-tastings`, { params }),

  createPreparedTasting: (venueId: number, data: Omit<CreatePreparedTastingRequest, 'venue_id'>) =>
    apiClient.post<PreparedTasting>(`/b2b/venues/${venueId}/prepared-tastings`, { ...data, venue_id: venueId }),

  getPreparedTasting: (uuid: string) =>
    apiClient.get<PreparedTasting>(`/b2b/prepared-tastings/${uuid}`),

  cancelPreparedTasting: (uuid: string) =>
    apiClient.delete(`/b2b/prepared-tastings/${uuid}`),
};

// ==================== B2B Monitoring API (for tenant owner/admin users) ====================

import type { LogEntry, LogFilter, LogStats, PerformanceMetrics, ProcessStatus, JobExecution } from '../types';

export const monitoringAPI = {
  // Logs
  getLogs: (filter?: LogFilter) =>
    apiClient.get<{ logs: LogEntry[]; total: number }>('/b2b/monitoring/logs', { params: filter }),

  getLogStats: (period?: string) =>
    apiClient.get<LogStats>('/b2b/monitoring/logs/stats', { params: { period } }),

  getLogSources: () =>
    apiClient.get<string[]>('/b2b/monitoring/logs/sources'),

  exportLogs: (filter: LogFilter, format: 'csv' | 'json') =>
    apiClient.post('/b2b/monitoring/logs/export', { filter, format }, { responseType: 'blob' }),

  // Metrics
  getMetrics: () =>
    apiClient.get<PerformanceMetrics>('/b2b/monitoring/metrics'),

  // Processes
  getProcesses: () =>
    apiClient.get<ProcessStatus>('/b2b/monitoring/processes'),

  // Workers
  triggerWorker: (workerName: string) =>
    apiClient.post(`/b2b/monitoring/workers/${workerName}/trigger`),

  getWorkerHistory: (workerName: string, limit?: number) =>
    apiClient.get<JobExecution[]>(`/b2b/monitoring/workers/${workerName}/history`, { params: { limit } }),
};

// ==================== Venue Tasting Events API ====================

export type VenueTastingEventStatus = 'scheduled' | 'active' | 'completed' | 'cancelled';
export type VenueTastingEventMode = 'sync' | 'async' | 'hybrid';
export type VenueEventGuestStatus = 'invited' | 'accepted' | 'declined' | 'joined' | 'completed';

export interface VenueTastingEvent {
  id: number;
  uuid: string;
  venue_id: number;
  tenant_id: number;
  host_user_id: number;
  name: string;
  description?: string;
  short_description?: string;
  scheduled_at?: string;
  tasting_mode: VenueTastingEventMode;
  max_participants: number;
  current_participants: number;
  template_id?: number;
  access_code: string;
  status: VenueTastingEventStatus;
  // Public event fields
  is_public: boolean;
  event_slug?: string;
  ticket_price?: number;
  booking_deadline?: string;
  published_at?: string;
  // Timestamps
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  // Relations
  venue_name?: string;
  venue_slug?: string;
  host_name?: string;
  guests?: VenueEventGuest[];
  spirits?: VenueEventSpirit[];
  branding?: VenueEventBranding;
  // Computed
  booking_count?: number;
}

export interface VenueEventGuest {
  id: number;
  event_id: number;
  session_id?: number;
  email?: string;
  name?: string;
  invitation_token?: string;
  status: VenueEventGuestStatus;
  is_anonymous: boolean;
  invited_at?: string;
  joined_at?: string;
  completed_at?: string;
}

export interface VenueEventSpirit {
  id: number;
  event_id: number;
  menu_item_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  spirit_type?: string;
  tasting_order: number;
  known_botanicals?: string[];
}

export interface VenueEventBranding {
  id: number;
  event_id: number;
  logo_url?: string;
  cover_image_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  font_family?: string;
  custom_css?: string;
  intro_text?: string;
  outro_text?: string;
  use_venue_defaults: boolean;
}

export interface VenueEventProgress {
  event_id: number;
  event_status: VenueTastingEventStatus;
  total_guests: number;
  joined_guests: number;
  completed_guests: number;
  current_step?: TastingStep;
  is_locked: boolean;
  moderator_message?: string;
  guests: VenueEventGuestProgress[];
}

export interface VenueEventGuestProgress {
  guest_id: number;
  guest_name?: string;
  status: VenueEventGuestStatus;
  current_step?: TastingStep;
  is_anonymous: boolean;
  joined_at?: string;
  completed_at?: string;
}

export type VenueEventBookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'no_show';
export type VenueEventPaymentStatus = 'pending' | 'paid' | 'refunded' | 'failed';

export interface VenueEventBooking {
  id: number;
  event_id: number;
  guest_id?: number;
  email: string;
  name: string;
  phone?: string;
  num_seats: number;
  total_price?: number;
  payment_status: VenueEventPaymentStatus;
  payment_reference?: string;
  status: VenueEventBookingStatus;
  confirmation_token: string;
  confirmed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  marketing_consent: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateVenueTastingEventRequest {
  name: string;
  description?: string;
  short_description?: string;
  scheduled_at?: string;
  tasting_mode?: VenueTastingEventMode;
  max_participants?: number;
  template_id?: number;
  // Public event fields
  is_public?: boolean;
  event_slug?: string;
  ticket_price?: number;
  booking_deadline?: string;
}

export interface UpdateVenueTastingEventRequest {
  name?: string;
  description?: string;
  short_description?: string;
  scheduled_at?: string;
  tasting_mode?: VenueTastingEventMode;
  max_participants?: number;
  // Public event fields
  is_public?: boolean;
  event_slug?: string;
  ticket_price?: number;
  booking_deadline?: string;
}

export interface InviteGuestsRequest {
  emails: string[];
}

export interface LockStepRequest {
  step: TastingStep;
  message?: string;
}

export interface BroadcastMessageRequest {
  message: string;
}

export interface SaveBrandingRequest {
  logo_url?: string;
  cover_image_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  font_family?: string;
  custom_css?: string;
  intro_text?: string;
  outro_text?: string;
  use_venue_defaults?: boolean;
}

export interface AddEventSpiritRequest {
  menu_item_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  spirit_type?: string;
  tasting_order?: number;
  known_botanicals?: string[];
}

export const venueTastingEventAPI = {
  // Event Management (venue-scoped)
  listEvents: (venueId: number, params?: { status?: VenueTastingEventStatus; limit?: number; offset?: number }) =>
    apiClient.get<PaginatedResponse<VenueTastingEvent>>(`/b2b/venues/${venueId}/events`, { params }),

  createEvent: (venueId: number, data: CreateVenueTastingEventRequest) =>
    apiClient.post<VenueTastingEvent>(`/b2b/venues/${venueId}/events`, data),

  // Event CRUD (event-scoped)
  getEvent: (eventId: number) =>
    apiClient.get<VenueTastingEvent>(`/b2b/venue-events/${eventId}`),

  updateEvent: (eventId: number, data: UpdateVenueTastingEventRequest) =>
    apiClient.put<VenueTastingEvent>(`/b2b/venue-events/${eventId}`, data),

  deleteEvent: (eventId: number) =>
    apiClient.delete(`/b2b/venue-events/${eventId}`),

  // Guest Management
  inviteGuests: (eventId: number, data: InviteGuestsRequest) =>
    apiClient.post<VenueEventGuest[]>(`/b2b/venue-events/${eventId}/invite`, data),

  removeGuest: (eventId: number, guestId: number) =>
    apiClient.delete(`/b2b/venue-events/${eventId}/guests/${guestId}`),

  // Moderation
  startEvent: (eventId: number) =>
    apiClient.post(`/b2b/venue-events/${eventId}/start`),

  lockStep: (eventId: number, data: LockStepRequest) =>
    apiClient.post(`/b2b/venue-events/${eventId}/lock`, data),

  unlockStep: (eventId: number) =>
    apiClient.post(`/b2b/venue-events/${eventId}/unlock`),

  advanceAll: (eventId: number) =>
    apiClient.post(`/b2b/venue-events/${eventId}/advance`),

  broadcastMessage: (eventId: number, data: BroadcastMessageRequest) =>
    apiClient.post(`/b2b/venue-events/${eventId}/broadcast`, data),

  getProgress: (eventId: number) =>
    apiClient.get<VenueEventProgress>(`/b2b/venue-events/${eventId}/progress`),

  completeEvent: (eventId: number) =>
    apiClient.post(`/b2b/venue-events/${eventId}/complete`),

  cancelEvent: (eventId: number) =>
    apiClient.post(`/b2b/venue-events/${eventId}/cancel`),

  // Public Event Management (publish/unpublish/bookings)
  publishEvent: (eventId: number) =>
    apiClient.post<VenueTastingEvent>(`/b2b/tasting-events/${eventId}/publish`),

  unpublishEvent: (eventId: number) =>
    apiClient.post<VenueTastingEvent>(`/b2b/tasting-events/${eventId}/unpublish`),

  // Bookings (for public events)
  listBookings: (eventId: number) =>
    apiClient.get<VenueEventBooking[]>(`/b2b/tasting-events/${eventId}/bookings`),

  getBookingStats: (eventId: number) =>
    apiClient.get<{ total: number; confirmed: number; pending: number; cancelled: number }>(`/b2b/tasting-events/${eventId}/bookings/stats`),

  confirmBooking: (eventId: number, bookingId: number) =>
    apiClient.post(`/b2b/tasting-events/${eventId}/bookings/${bookingId}/confirm`),

  cancelBooking: (eventId: number, bookingId: number) =>
    apiClient.post(`/b2b/tasting-events/${eventId}/bookings/${bookingId}/cancel`),

  markNoShow: (eventId: number, bookingId: number) =>
    apiClient.post(`/b2b/tasting-events/${eventId}/bookings/${bookingId}/no-show`),

  // Branding
  saveBranding: (eventId: number, data: SaveBrandingRequest) =>
    apiClient.put<VenueEventBranding>(`/b2b/venue-events/${eventId}/branding`, data),

  getBranding: (eventId: number) =>
    apiClient.get<VenueEventBranding>(`/b2b/venue-events/${eventId}/branding`),

  // Spirit Management
  addSpirit: (eventId: number, data: AddEventSpiritRequest) =>
    apiClient.post<VenueEventSpirit>(`/b2b/venue-events/${eventId}/spirits`, data),

  removeSpirit: (eventId: number, spiritId: number) =>
    apiClient.delete(`/b2b/venue-events/${eventId}/spirits/${spiritId}`),

  reorderSpirits: (eventId: number, spiritIds: number[]) =>
    apiClient.put(`/b2b/venue-events/${eventId}/spirits/reorder`, { spirit_ids: spiritIds }),

  listSpirits: (eventId: number) =>
    apiClient.get<VenueEventSpirit[]>(`/b2b/venue-events/${eventId}/spirits`),
};

// ==================== Public Events API (No Auth Required) ====================

import type {
  PublicEventListItem,
  PublicEventResponse,
  EventAvailabilityResponse,
  BookEventRequest,
  BookingConfirmationResponse,
  PublicBooking,
} from '../types';

export const publicEventsAPI = {
  // List public events for a venue
  listEvents: (venueSlug: string) =>
    apiClient.get<PublicEventListItem[]>(`/public/venue/${venueSlug}/events`),

  // Get single event details
  getEvent: (venueSlug: string, eventSlug: string) =>
    apiClient.get<PublicEventResponse>(`/public/venue/${venueSlug}/events/${eventSlug}`),

  // Check availability
  getAvailability: (venueSlug: string, eventSlug: string) =>
    apiClient.get<EventAvailabilityResponse>(`/public/venue/${venueSlug}/events/${eventSlug}/availability`),

  // Book event
  bookEvent: (venueSlug: string, eventSlug: string, data: BookEventRequest) =>
    apiClient.post<BookingConfirmationResponse>(`/public/venue/${venueSlug}/events/${eventSlug}/book`, data),

  // Get booking by confirmation token
  getBooking: (token: string) =>
    apiClient.get<PublicBooking>(`/public/bookings/${token}`),

  // Cancel booking
  cancelBooking: (token: string) =>
    apiClient.post<{ message: string }>(`/public/bookings/${token}/cancel`),

  // Waitlist
  joinWaitlist: (venueSlug: string, eventSlug: string, data: { email: string; name: string; num_seats: number }) =>
    apiClient.post<{ message: string; position: number; email: string }>(`/public/venue/${venueSlug}/events/${eventSlug}/waitlist`, data),

  getWaitlistPosition: (venueSlug: string, eventSlug: string, email: string) =>
    apiClient.get<{ position: number; status: string; total_waiting: number }>(`/public/venue/${venueSlug}/events/${eventSlug}/waitlist/position`, { params: { email } }),
};

// ==================== Inventory API ====================

import type {
  InventoryItem,
  InventoryMovement,
  InventoryValue,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  AdjustStockRequest,
} from '../types';

export const inventoryAPI = {
  // List inventory items for a venue
  listItems: (venueId: number, limit = 20, offset = 0) =>
    apiClient.get<{ data: InventoryItem[]; pagination: { total: number; limit: number; offset: number } }>(`/b2b/venues/${venueId}/inventory`, {
      params: { limit, offset },
    }),

  // Create new inventory item
  createItem: (venueId: number, data: CreateInventoryItemRequest) =>
    apiClient.post<InventoryItem>(`/b2b/venues/${venueId}/inventory`, data),

  // Get low stock items
  getLowStock: (venueId: number) =>
    apiClient.get<InventoryItem[]>(`/b2b/venues/${venueId}/inventory/low-stock`),

  // Get inventory value
  getInventoryValue: (venueId: number) =>
    apiClient.get<InventoryValue>(`/b2b/venues/${venueId}/inventory/value`),

  // Get single inventory item
  getItem: (itemId: number) =>
    apiClient.get<InventoryItem>(`/b2b/inventory/${itemId}`),

  // Update inventory item
  updateItem: (itemId: number, data: UpdateInventoryItemRequest) =>
    apiClient.put<InventoryItem>(`/b2b/inventory/${itemId}`, data),

  // Delete inventory item
  deleteItem: (itemId: number) =>
    apiClient.delete(`/b2b/inventory/${itemId}`),

  // Adjust stock
  adjustStock: (itemId: number, data: AdjustStockRequest) =>
    apiClient.post<InventoryItem>(`/b2b/inventory/${itemId}/adjust`, data),

  // Get movements for an item
  getMovements: (itemId: number, limit = 50, offset = 0) =>
    apiClient.get<InventoryMovement[]>(`/b2b/inventory/${itemId}/movements`, {
      params: { limit, offset },
    }),

  // Record count (physical inventory count)
  recordCount: (itemId: number, actualStock: number, notes?: string) =>
    apiClient.post<InventoryItem>(`/b2b/inventory/${itemId}/count`, {
      actual_stock: actualStock,
      notes,
    }),
};

// ==================== Inventory Light API ====================

import type {
  InventoryProduct,
  StockMovement,
  InventorySession,
  InventorySummary,
  LowStockAlert,
  InventoryDifference,
  CreateInventoryProductRequest,
  UpdateInventoryProductRequest,
  CreateStockMovementRequest,
  CreateInventorySessionRequest,
} from '../types';

export const inventoryLightAPI = {
  // ==================== Products ====================

  listProducts: (venueId: number, params?: {
    limit?: number;
    offset?: number;
    category?: string;
    is_active?: boolean;
    low_stock_only?: boolean;
    search?: string;
  }) =>
    apiClient.get<{ data: InventoryProduct[]; total: number; limit: number; offset: number }>(
      `/b2b/venues/${venueId}/inventory/products`,
      { params }
    ),

  getProduct: (venueId: number, productId: number) =>
    apiClient.get<InventoryProduct>(`/b2b/venues/${venueId}/inventory/products/${productId}`),

  createProduct: (venueId: number, data: CreateInventoryProductRequest) =>
    apiClient.post<InventoryProduct>(`/b2b/venues/${venueId}/inventory/products`, data),

  updateProduct: (venueId: number, productId: number, data: UpdateInventoryProductRequest) =>
    apiClient.put<InventoryProduct>(`/b2b/venues/${venueId}/inventory/products/${productId}`, data),

  deleteProduct: (venueId: number, productId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/inventory/products/${productId}`),

  getProductByEAN: (venueId: number, ean: string) =>
    apiClient.get<InventoryProduct>(`/b2b/venues/${venueId}/inventory/products/ean/${ean}`),

  // ==================== Stock Movements ====================

  listMovements: (venueId: number, params?: {
    limit?: number;
    offset?: number;
    product_id?: number;
    movement_type?: string;
    from_date?: string;
    to_date?: string;
  }) =>
    apiClient.get<{ data: StockMovement[]; total: number; limit: number; offset: number }>(
      `/b2b/venues/${venueId}/inventory/movements`,
      { params }
    ),

  createMovement: (venueId: number, data: CreateStockMovementRequest) =>
    apiClient.post<StockMovement>(`/b2b/venues/${venueId}/inventory/movements`, data),

  // ==================== Inventory Sessions ====================

  listSessions: (venueId: number, params?: {
    limit?: number;
    offset?: number;
    status?: string;
  }) =>
    apiClient.get<{ data: InventorySession[]; total: number; limit: number; offset: number }>(
      `/b2b/venues/${venueId}/inventory/sessions`,
      { params }
    ),

  getSession: (venueId: number, sessionId: number) =>
    apiClient.get<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions/${sessionId}`),

  createSession: (venueId: number, data: CreateInventorySessionRequest) =>
    apiClient.post<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions`, data),

  createQuickSession: (venueId: number, data?: { notes?: string }) =>
    apiClient.post<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions/quick`, data || {}),

  startSession: (venueId: number, sessionId: number) =>
    apiClient.post<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions/${sessionId}/start`),

  updateCount: (venueId: number, sessionId: number, itemId: number, data: Omit<UpdateInventoryCountRequest, 'product_id' | 'counted_quantity'>) =>
    apiClient.put<{ message: string }>(
      `/b2b/venues/${venueId}/inventory/sessions/${sessionId}/items/${itemId}`,
      data
    ),

  completeSession: (venueId: number, sessionId: number) =>
    apiClient.post<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions/${sessionId}/complete`),

  cancelSession: (venueId: number, sessionId: number) =>
    apiClient.post<InventorySession>(`/b2b/venues/${venueId}/inventory/sessions/${sessionId}/cancel`),

  getSessionDifferences: (venueId: number, sessionId: number) =>
    apiClient.get<InventoryDifference[]>(`/b2b/venues/${venueId}/inventory/sessions/${sessionId}/differences`),

  // ==================== Summary & Alerts ====================

  getSummary: (venueId: number) =>
    apiClient.get<InventorySummary>(`/b2b/venues/${venueId}/inventory/summary`),

  getLowStockAlerts: (venueId: number) =>
    apiClient.get<LowStockAlert[]>(`/b2b/venues/${venueId}/inventory/alerts/low-stock`),

  // ==================== Menu Integration ====================

  addProductsToMenu: (venueId: number, data: {
    category_id: number;
    product_ids: number[];
    price_strategy: 'purchase_price' | 'purchase_price_with_markup';
    markup_percentage?: number;
  }) => apiClient.post<{
    created_count: number;
    skipped_count: number;
    created_items: Array<{ id: number; name: string; price: number }>;
    skipped_items: Array<{ product_id: number; name: string; reason: string }>;
  }>(`/b2b/venues/${venueId}/inventory/products/to-menu`, data),
};

// ==================== Recipe & Pour Cost API ====================

export const recipeAPI = {
  // Recipe ingredients for a menu item
  getRecipe: (menuItemId: number) =>
    apiClient.get<MenuRecipeIngredient[]>(`/b2b/menu-items/${menuItemId}/recipe`),

  addIngredient: (menuItemId: number, data: CreateRecipeIngredientRequest) =>
    apiClient.post<MenuRecipeIngredient>(`/b2b/menu-items/${menuItemId}/recipe`, data),

  setRecipe: (menuItemId: number, data: SetRecipeRequest) =>
    apiClient.put<MenuRecipeIngredient[]>(`/b2b/menu-items/${menuItemId}/recipe`, data),

  updateIngredient: (menuItemId: number, ingredientId: number, data: Partial<CreateRecipeIngredientRequest>) =>
    apiClient.put<MenuRecipeIngredient>(`/b2b/menu-items/${menuItemId}/recipe/${ingredientId}`, data),

  deleteIngredient: (menuItemId: number, ingredientId: number) =>
    apiClient.delete(`/b2b/menu-items/${menuItemId}/recipe/${ingredientId}`),

  // Cost calculation
  getItemCost: (menuItemId: number) =>
    apiClient.get<RecipeCostCalculation>(`/b2b/menu-items/${menuItemId}/cost`),

  // Pour cost overview for a venue
  getPourCostOverview: (venueId: number) =>
    apiClient.get<PourCostOverview>(`/b2b/venues/${venueId}/pour-cost`),

  // Wareneinsatz report
  getWareneinsatzReport: (venueId: number, dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    const qs = params.toString();
    return apiClient.get<WareneinsatzReport>(`/b2b/venues/${venueId}/wareneinsatz${qs ? `?${qs}` : ''}`);
  },
};

// ==================== Recommendation API ====================

import type {
  MenuItemRecommendation,
  CreateRecommendationRequest,
  SetRecommendationsRequest,
  UpdateRecommendationRequest,
} from '../types';

export const recommendationAPI = {
  // All active recommendations of a venue (Live-Orders/KDS combine spirit + mixer)
  listForVenue: (venueId: number) =>
    apiClient.get<MenuItemRecommendation[]>(`/b2b/venues/${venueId}/recommendations`),

  // Mixing instructions per combo label ({spirit}/{mixer} placeholders)
  listComboHints: (venueId: number) =>
    apiClient.get<{ label: string; hint: string }[]>(`/b2b/venues/${venueId}/combo-hints`),

  // List recommendations for a menu item
  list: (menuItemId: number) =>
    apiClient.get<MenuItemRecommendation[]>(`/b2b/menu-items/${menuItemId}/recommendations`),

  // Add a single recommendation
  create: (menuItemId: number, data: CreateRecommendationRequest) =>
    apiClient.post<MenuItemRecommendation>(`/b2b/menu-items/${menuItemId}/recommendations`, data),

  // Replace all recommendations (bulk)
  setAll: (menuItemId: number, data: SetRecommendationsRequest) =>
    apiClient.put<MenuItemRecommendation[]>(`/b2b/menu-items/${menuItemId}/recommendations`, data),

  // Update a single recommendation
  update: (menuItemId: number, recId: number, data: UpdateRecommendationRequest) =>
    apiClient.put<MenuItemRecommendation>(`/b2b/menu-items/${menuItemId}/recommendations/${recId}`, data),

  // Delete a single recommendation
  remove: (menuItemId: number, recId: number) =>
    apiClient.delete(`/b2b/menu-items/${menuItemId}/recommendations/${recId}`),
};

// ==================== Happy Hour API ====================

export interface HappyHourRule {
  id: number;
  uuid: string;
  tenant_id: number;
  venue_id: number;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_price' | 'fixed_discount';
  discount_value: number;
  applies_to: 'all' | 'category' | 'item';
  category_id?: number;
  item_id?: number;
  start_time: string; // HH:MM
  end_time: string;   // HH:MM
  days_of_week: number[]; // 0=Sunday, 6=Saturday
  valid_from?: string; // YYYY-MM-DD
  valid_until?: string; // YYYY-MM-DD
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Loaded relations
  category_name?: string;
  item_name?: string;
}

export interface CreateHappyHourRuleRequest {
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_price' | 'fixed_discount';
  discount_value: number;
  applies_to: 'all' | 'category' | 'item';
  category_id?: number;
  item_id?: number;
  start_time: string;
  end_time: string;
  days_of_week: number[];
  valid_from?: string;
  valid_until?: string;
  priority: number;
  is_active: boolean;
}

export interface UpdateHappyHourRuleRequest extends CreateHappyHourRuleRequest {}

export const happyHourAPI = {
  // List all rules for a venue
  listRules: (venueId: number) =>
    apiClient.get<HappyHourRule[]>(`/b2b/venues/${venueId}/happy-hour-rules`),

  // Get a single rule
  getRule: (ruleId: number) =>
    apiClient.get<HappyHourRule>(`/b2b/happy-hour-rules/${ruleId}`),

  // Create a new rule
  createRule: (venueId: number, data: CreateHappyHourRuleRequest) =>
    apiClient.post<HappyHourRule>(`/b2b/venues/${venueId}/happy-hour-rules`, data),

  // Update a rule
  updateRule: (ruleId: number, data: UpdateHappyHourRuleRequest) =>
    apiClient.put<HappyHourRule>(`/b2b/happy-hour-rules/${ruleId}`, data),

  // Delete a rule
  deleteRule: (ruleId: number) =>
    apiClient.delete(`/b2b/happy-hour-rules/${ruleId}`),

  // Get active rules for a venue (currently active based on time)
  getActiveRules: (venueId: number) =>
    apiClient.get<HappyHourRule[]>(`/b2b/venues/${venueId}/happy-hour-rules/active`),

  // Toggle rule active status
  toggleRuleStatus: (ruleId: number, isActive: boolean) =>
    apiClient.post<HappyHourRule>(`/b2b/happy-hour-rules/${ruleId}/toggle`, { is_active: isActive }),
};

// =====================================================
// Venue Marketing / Newsletter API
// =====================================================

export interface VenueSubscriber {
  id: number;
  tenant_id: number;
  venue_id: number;
  email: string;
  guest_name?: string;
  status: 'pending' | 'subscribed' | 'unsubscribed' | 'bounced';
  source: 'order' | 'event_booking' | 'manual' | 'import' | 'qr_code';
  opted_in_at?: string;
  opted_out_at?: string;
  order_count: number;
  total_spent: number;
  first_order_at?: string;
  last_order_at?: string;
  is_stammgast: boolean;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface VenueSubscriberStats {
  venue_id: number;
  total_subscribers: number;
  active_subscribers: number;
  pending_subscribers: number;
  unsubscribed_count: number;
  bounced_count: number;
  stammgast_count: number;
  new_this_month: number;
  source_breakdown: Record<string, number>;
}

export interface VenueEmailTemplate {
  id: number;
  tenant_id: number;
  venue_id: number;
  name: string;
  subject: string;
  html_content: string;
  template_type: 'newsletter' | 'promotion' | 'event' | 'welcome' | 'custom';
  variables?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SegmentCriteria {
  guest_type?: string;
  min_order_count?: number;
  min_total_spent?: number;
  last_visit_days?: number;
  tags?: string[];
}

export interface VenueCampaign {
  id: number;
  tenant_id: number;
  venue_id: number;
  template_id?: number;
  name: string;
  subject: string;
  html_content?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
  segment_criteria?: SegmentCriteria;
  scheduled_at?: string;
  sent_at?: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  open_count: number;
  click_count: number;
  unsubscribe_count: number;
  created_at: string;
  updated_at: string;
}

export interface VenueCampaignSend {
  id: number;
  campaign_id: number;
  subscriber_id: number;
  tracking_id: string;
  status: 'pending' | 'sent' | 'failed' | 'bounced';
  error_message?: string;
  sent_at?: string;
  opened_at?: string;
  clicked_at?: string;
  open_count: number;
  click_count: number;
  email: string;
  guest_name?: string;
  created_at: string;
}

export const marketingAPI = {
  // List subscribers with filtering
  listSubscribers: (venueId: number, params?: {
    status?: string;
    source?: string;
    stammgast?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get<VenueSubscriber[]>(`/b2b/venues/${venueId}/marketing/subscribers`, { params }),

  // Create subscriber manually
  createSubscriber: (venueId: number, data: { email: string; guest_name?: string; tags?: string[] }) =>
    apiClient.post<VenueSubscriber>(`/b2b/venues/${venueId}/marketing/subscribers`, data),

  // Get subscriber statistics
  getStats: (venueId: number) =>
    apiClient.get<VenueSubscriberStats>(`/b2b/venues/${venueId}/marketing/subscribers/stats`),

  // Export subscribers as CSV
  exportSubscribers: (venueId: number) =>
    apiClient.get(`/b2b/venues/${venueId}/marketing/subscribers/export`, { responseType: 'blob' }),

  // Import from guest list
  importFromGuestList: (venueId: number, sendConfirmation: boolean) =>
    apiClient.post(`/b2b/venues/${venueId}/marketing/subscribers/import`, { send_confirmation: sendConfirmation }),

  // Unsubscribe (delete) subscriber
  deleteSubscriber: (venueId: number, subscriberId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/marketing/subscribers/${subscriberId}`),

  // ---- Templates ----
  listTemplates: (venueId: number) =>
    apiClient.get<VenueEmailTemplate[]>(`/b2b/venues/${venueId}/marketing/templates`),

  getTemplate: (venueId: number, templateId: number) =>
    apiClient.get<VenueEmailTemplate>(`/b2b/venues/${venueId}/marketing/templates/${templateId}`),

  createTemplate: (venueId: number, data: {
    name: string;
    subject: string;
    html_content: string;
    template_type?: string;
    variables?: string[];
  }) =>
    apiClient.post<VenueEmailTemplate>(`/b2b/venues/${venueId}/marketing/templates`, data),

  updateTemplate: (venueId: number, templateId: number, data: {
    name?: string;
    subject?: string;
    html_content?: string;
    template_type?: string;
    variables?: string[];
    is_active?: boolean;
  }) =>
    apiClient.put<VenueEmailTemplate>(`/b2b/venues/${venueId}/marketing/templates/${templateId}`, data),

  deleteTemplate: (venueId: number, templateId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/marketing/templates/${templateId}`),

  // ---- Campaigns ----
  listCampaigns: (venueId: number, params?: { limit?: number; offset?: number }) =>
    apiClient.get<VenueCampaign[]>(`/b2b/venues/${venueId}/marketing/campaigns`, { params }),

  getCampaign: (venueId: number, campaignId: number) =>
    apiClient.get<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}`),

  createCampaign: (venueId: number, data: {
    name: string;
    subject: string;
    template_id?: number;
    html_content?: string;
    segment_criteria?: SegmentCriteria;
  }) =>
    apiClient.post<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns`, data),

  updateCampaign: (venueId: number, campaignId: number, data: {
    name?: string;
    subject?: string;
    template_id?: number;
    html_content?: string;
    segment_criteria?: SegmentCriteria;
  }) =>
    apiClient.put<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}`, data),

  deleteCampaign: (venueId: number, campaignId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}`),

  scheduleCampaign: (venueId: number, campaignId: number, scheduledAt: string) =>
    apiClient.post<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}/schedule`, { scheduled_at: scheduledAt }),

  sendCampaignNow: (venueId: number, campaignId: number) =>
    apiClient.post<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}/send`),

  cancelCampaign: (venueId: number, campaignId: number) =>
    apiClient.post<VenueCampaign>(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}/cancel`),

  getCampaignSends: (venueId: number, campaignId: number, params?: { limit?: number; offset?: number }) =>
    apiClient.get(`/b2b/venues/${venueId}/marketing/campaigns/${campaignId}/sends`, { params }),
};

// ==================== Table Management API ====================

export const tableAPI = {
  // Tables
  listTables: (venueId: number) =>
    apiClient.get<VenueTable[]>(`/b2b/venues/${venueId}/tables`),

  createTable: (venueId: number, data: CreateTableRequest) =>
    apiClient.post<VenueTable>(`/b2b/venues/${venueId}/tables`, data),

  getTable: (venueId: number, tableId: number) =>
    apiClient.get<VenueTable>(`/b2b/venues/${venueId}/tables/${tableId}`),

  updateTable: (venueId: number, tableId: number, data: UpdateTableRequest) =>
    apiClient.put<VenueTable>(`/b2b/venues/${venueId}/tables/${tableId}`, data),

  deleteTable: (venueId: number, tableId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/tables/${tableId}`),

  updateTablePosition: (venueId: number, tableId: number, positionX: number, positionY: number) =>
    apiClient.patch(`/b2b/venues/${venueId}/tables/${tableId}/position`, {
      position_x: positionX,
      position_y: positionY,
    }),

  updateTableStatus: (venueId: number, tableId: number, status: string) =>
    apiClient.patch(`/b2b/venues/${venueId}/tables/${tableId}/status`, { status }),

  getTablePlan: (venueId: number) =>
    apiClient.get<TablePlanData>(`/b2b/venues/${venueId}/table-plan`),

  // Reservations
  listReservations: (venueId: number, params?: { date?: string; status?: string; limit?: number; offset?: number }) =>
    apiClient.get<Reservation[]>(`/b2b/venues/${venueId}/reservations`, { params }),

  createReservation: (venueId: number, data: CreateReservationRequest) =>
    apiClient.post<Reservation>(`/b2b/venues/${venueId}/reservations`, data),

  getReservation: (venueId: number, reservationId: number) =>
    apiClient.get<Reservation>(`/b2b/venues/${venueId}/reservations/${reservationId}`),

  updateReservation: (venueId: number, reservationId: number, data: UpdateReservationRequest) =>
    apiClient.put<Reservation>(`/b2b/venues/${venueId}/reservations/${reservationId}`, data),

  updateReservationStatus: (venueId: number, reservationId: number, status: string) =>
    apiClient.patch(`/b2b/venues/${venueId}/reservations/${reservationId}/status`, { status }),

  deleteReservation: (venueId: number, reservationId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/reservations/${reservationId}`),
};

// ==================== Guest Tabs API ====================

export const guestTabAPI = {
  listTabs: (venueId: number, params?: { status?: string; table_number?: string; limit?: number; offset?: number }) =>
    apiClient.get(`/b2b/venues/${venueId}/tabs`, { params }),

  listOpenTabs: (venueId: number) =>
    apiClient.get(`/b2b/venues/${venueId}/tabs/open`),

  createTab: (venueId: number, data: { table_number: string; guest_name?: string; guest_email?: string; notes?: string }) =>
    apiClient.post(`/b2b/venues/${venueId}/tabs`, data),

  getTab: (venueId: number, tabId: number) =>
    apiClient.get(`/b2b/venues/${venueId}/tabs/${tabId}`),

  recordPayment: (venueId: number, tabId: number, data: { amount: number; tip_amount: number; payment_method: string; split_label?: string; notes?: string; items?: { order_item_id: number; quantity: number; amount: number }[] }) =>
    apiClient.post(`/b2b/venues/${venueId}/tabs/${tabId}/payments`, data),

  splitEqual: (venueId: number, tabId: number, data: { number_of_guests: number; tip_total: number; payment_method: string }) =>
    apiClient.post(`/b2b/venues/${venueId}/tabs/${tabId}/split-equal`, data),

  voidPayment: (venueId: number, tabId: number, paymentId: number) =>
    apiClient.delete(`/b2b/venues/${venueId}/tabs/${tabId}/payments/${paymentId}`),

  closeTab: (venueId: number, tabId: number) =>
    apiClient.post(`/b2b/venues/${venueId}/tabs/${tabId}/close`),

  cancelTab: (venueId: number, tabId: number) =>
    apiClient.post(`/b2b/venues/${venueId}/tabs/${tabId}/cancel`),

  moveTab: (venueId: number, tabId: number, tableNumber: string) =>
    apiClient.patch(`/b2b/venues/${venueId}/tabs/${tabId}/move`, { table_number: tableNumber }),
};

// ==================== Team Management API ====================

export const teamAPI = {
  listUsers: () =>
    apiClient.get('/users'),

  inviteUser: (data: InviteUserRequest) =>
    apiClient.post('/users/invite', data),

  updateUser: (id: number, data: UpdateUserRequest) =>
    apiClient.put(`/users/${id}`, data),

  deleteUser: (id: number) =>
    apiClient.delete(`/users/${id}`),

  resetPassword: (id: number) =>
    apiClient.post(`/users/${id}/reset-password`),
};

export const releaseNoteAPI = {
  list: () => apiClient.get('/b2b/release-notes'),
  listAll: () => apiClient.get('/b2b/release-notes/admin/all'),
  get: (id: number) => apiClient.get(`/b2b/release-notes/${id}`),
  create: (data: import('../types').CreateReleaseNoteRequest) =>
    apiClient.post('/b2b/release-notes', data),
  update: (id: number, data: import('../types').UpdateReleaseNoteRequest) =>
    apiClient.put(`/b2b/release-notes/${id}`, data),
  remove: (id: number) => apiClient.delete(`/b2b/release-notes/${id}`),
};
